import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import {
    CheckCircle2,
    Clock,
    XCircle,
    LogOut,
    Settings,
    Copy,
    Search,
    Link as LinkIcon,
    TrendingUp,
    Wallet,
    Filter,
    Download,
    Store,
    QrCode,
    Shield,
    LogIn,
    Calendar as CalendarIcon,
    Eye,
    Paperclip,
    Upload,
    Pencil,
    FileText,
    Key,
    Crown,
    AlertTriangle,
    X,
    User as UserIcon,
} from "lucide-react";

import { api, API, formatCRC, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Header from "../components/sinpe/Header";
import Footer from "../components/sinpe/Footer";
import SalesChart from "../components/sinpe/SalesChart";
import StaffManager from "../components/sinpe/StaffManager";
import OnboardingBanner from "../components/sinpe/OnboardingBanner";
import { useRealtimeTx } from "../lib/useRealtimeTx";
import { playCashRegister } from "../lib/cashSound";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";

const StatCard = ({ icon: Icon, label, value, accent }) => (
    <div className="card-clean p-6 flex flex-col gap-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-[#64748B]">{label}</p>
            <div className={`h-10 w-10 rounded-full ${accent} flex items-center justify-center`}>
                <Icon className="w-5 h-5" strokeWidth={1.75} />
            </div>
        </div>
        <p className="font-display text-3xl md:text-4xl font-semibold text-[#022C22] tracking-tight">{value}</p>
    </div>
);

const StatusBadge = ({ status }) => {
    if (status === "verified") {
        return (
            <span className="badge-verified" data-testid="badge-verified">
                <CheckCircle2 className="w-3 h-3" /> Verificado
            </span>
        );
    }
    if (status === "failed") {
        return (
            <span className="badge-failed" data-testid="badge-failed">
                <XCircle className="w-3 h-3" /> Rechazado
            </span>
        );
    }
    if (status === "manual_review") {
        return (
            <span className="badge-pending" data-testid="badge-manual-review">
                <Eye className="w-3 h-3" /> Pendiente de revisión
            </span>
        );
    }
    return (
        <span className="badge-pending" data-testid="badge-pending">
            <Clock className="w-3 h-3" /> Pendiente
        </span>
    );
};

const WHATSAPP_UPGRADE_URL =
    "https://wa.me/50664021425?text=" +
    encodeURIComponent(
        "¡Hola! Quiero activar el Plan Pyme Pro en SINPE-Flow. Mi correo es: "
    );

// Onvo Pay Checkout URLs (configurable via env so the user can paste the
// real checkout-session links from the Onvo Dashboard without code changes).
const ONVO_CHECKOUT_PRO_100 =
    process.env.REACT_APP_ONVO_CHECKOUT_PRO_100 || "https://checkout.onvopay.com/";
const ONVO_CHECKOUT_PRO_1 =
    process.env.REACT_APP_ONVO_CHECKOUT_PRO_1 || "https://checkout.onvopay.com/";
const ONVO_CHECKOUT_PRO_2 =
    process.env.REACT_APP_ONVO_CHECKOUT_PRO_2 || "https://checkout.onvopay.com/";

const TIER_LABEL = {
    pro_100: "Plan Emprendedor",
    pro_1: "Pyme Pro · Nivel 1",
    pro_2: "Pyme Pro · Nivel 2",
};
const TIER_PRICE = { pro_100: "₡5.000", pro_1: "₡9.900", pro_2: "₡16.900" };
const TIER_LIMIT = { pro_100: 100, pro_1: 300, pro_2: 1200 };

const fmtSubscriptionDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString("es-CR", { day: "2-digit", month: "long", year: "numeric" });
    } catch {
        return null;
    }
};

// "1° de Julio" / "15 de Agosto" — for inline cycle banner microcopy
const fmtCycleDate = (iso) => {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        const day = d.getUTCDate();
        const month = d.toLocaleDateString("es-CR", { month: "long", timeZone: "UTC" });
        return day === 1 ? `1° de ${month}` : `${day} de ${month}`;
    } catch {
        return null;
    }
};

// Days remaining until ISO date (UTC-based, ceil to whole days)
const daysUntil = (iso) => {
    if (!iso) return null;
    try {
        const ms = new Date(iso).getTime() - Date.now();
        return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
    } catch {
        return null;
    }
};

const toYMD = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const fmtShort = (date) =>
    date
        ? new Date(date).toLocaleDateString("es-CR", { day: "2-digit", month: "short", year: "numeric" })
        : null;

export default function Dashboard() {
    const { user, setUser, logout, checkAuth } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        verified_today: 0,
        total_amount: 0,
        pending_count: 0,
        total_transactions: 0,
    });
    const [txs, setTxs] = useState([]);
    const [loadingTxs, setLoadingTxs] = useState(true);
    const [filterStatus, setFilterStatus] = useState("all");
    const [dateRange, setDateRange] = useState("all");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [search, setSearch] = useState("");
    const [reviewingId, setReviewingId] = useState(null);
    const [uploadingId, setUploadingId] = useState(null);
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    const [paymentSuccessOpen, setPaymentSuccessOpen] = useState(false);
    // Subscription management: cancel + upgrade-warning modals
    const [upgradeWarningOpen, setUpgradeWarningOpen] = useState(false);
    const [pendingUpgradeHref, setPendingUpgradeHref] = useState(null);
    const [pendingUpgradeLabel, setPendingUpgradeLabel] = useState("");
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [comprobanteOpen, setComprobanteOpen] = useState(null); // tx object or null
    const [editingTx, setEditingTx] = useState(null); // tx object or null
    const [editForm, setEditForm] = useState({ amount: "", reference: "", customer_name: "", numero_factura: "" });
    const [savingEdit, setSavingEdit] = useState(false);
    const fileInputRef = useRef(null);
    const pendingUploadTxRef = useRef(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [qrOpen, setQrOpen] = useState(false);
    const qrContainerRef = useRef(null);
    const [phone, setPhone] = useState(user?.phone_number || "");
    const [businessName, setBusinessName] = useState(user?.business_name || "");
    const [slug, setSlug] = useState(user?.slug || "");
    const [apiKey, setApiKey] = useState(user?.cybersinpe_api_key || "");
    const [notifyEmail, setNotifyEmail] = useState(user?.notify_email ?? true);
    const [notifySms, setNotifySms] = useState(user?.notify_sms ?? false);
    const [saving, setSaving] = useState(false);

    const paymentIdentifier = user?.slug || user?.user_id || "";
    const paymentLink = `${window.location.origin}/pay/${paymentIdentifier}`;

    const loadStats = useCallback(async () => {
        try {
            const { data } = await api.get("/transactions/stats");
            setStats(data);
            if (data.monthly_limit_reached) {
                // Show upgrade modal only once per session
                const seen = sessionStorage.getItem("sinpe_limit_modal_seen");
                if (!seen) {
                    setUpgradeOpen(true);
                    sessionStorage.setItem("sinpe_limit_modal_seen", "1");
                }
            }
        } catch (e) {
            console.warn("Failed to load stats:", e?.response?.data?.detail || e?.message);
        }
    }, []);

    const loadTxs = useCallback(async () => {
        setLoadingTxs(true);
        try {
            const params = {};
            if (filterStatus !== "all") params.status = filterStatus;
            if (startDate || endDate) {
                if (startDate) params.start_date = toYMD(startDate);
                if (endDate) params.end_date = toYMD(endDate);
            } else if (dateRange !== "all") {
                params.date_range = dateRange;
            }
            if (search.trim()) params.search = search.trim();
            const { data } = await api.get("/transactions", { params });
            setTxs(data);
        } catch (e) {
            toast.error("Error cargando transacciones");
        } finally {
            setLoadingTxs(false);
        }
    }, [filterStatus, dateRange, startDate, endDate, search]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    // Onvo Pay redirect handler: when the user returns from a successful
    // checkout (`?payment=success`), show a welcome modal, refresh user +
    // stats, and call the server-side salvage endpoint to activate the
    // subscription even if the customer paid in Onvo with a different email
    // than their Google login.
    //
    // Triple safety net:
    //   1. Onvo webhook (server-side, signed) — primary activation path
    //   2. checkAuth + loadStats refresh — shows the new tier instantly when (1) succeeded
    //   3. POST /v1/subscriptions/verify-session — claims an orphaned payment
    //      if the webhook arrived but couldn't match by email
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const paymentParam = params.get("payment");
        if (!paymentParam) return;

        if (paymentParam === "success") {
            toast.success("¡Pago exitoso! 🎉");
            setPaymentSuccessOpen(true);

            const runSalvageAndRefresh = async () => {
                try {
                    const { data } = await api.post("/v1/subscriptions/verify-session");
                    if (data?.status === "activated") {
                        // We salvaged an orphaned payment — inform the user
                        const planName = TIER_LABEL[data.tier] || "tu plan";
                        toast.success(`Activamos tu ${planName} automáticamente.`);
                    } else if (data?.already_active) {
                        // Webhook arrived first — nothing to do, just refresh
                    }
                    // else: data.status === "no_pending_payment" → wait for webhook
                } catch (e) {
                    // Salvage is best-effort; the webhook usually wins.
                    console.warn("[verify-session] failed (will rely on webhook):", e?.response?.data || e);
                }
                await checkAuth();
                await loadStats();
            };
            runSalvageAndRefresh();
            // One retry at 4s to cover the case where Onvo webhook arrives
            // slightly after the user lands on /dashboard.
            const retryTimer = setTimeout(runSalvageAndRefresh, 4000);

            // Clean the query param from the URL so a hard refresh doesn't
            // re-trigger the modal.
            window.history.replaceState({}, "", window.location.pathname);
            return () => clearTimeout(retryTimer);
        } else if (paymentParam === "cancelled" || paymentParam === "cancel") {
            toast.info("El pago fue cancelado. Podés intentarlo cuando quieras.");
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, [checkAuth, loadStats]);

    useEffect(() => {
        const t = setTimeout(loadTxs, 250);
        return () => clearTimeout(t);
    }, [loadTxs]);

    const isStaff = user?.role === "staff";

    // ─────────────── Subscription management handlers ───────────────
    // If user is already pro on a different tier, redirecting straight to
    // Onvo causes a second active subscription (double charge). Show the
    // warning modal first so they can cancel the previous one in Onvo.
    const handleUpgradeClick = (e, tier, href, label) => {
        const currentTier = stats?.subscription_tier;
        if (stats?.plan === "pro" && currentTier && currentTier !== tier) {
            e.preventDefault();
            setPendingUpgradeHref(href);
            setPendingUpgradeLabel(label);
            setUpgradeWarningOpen(true);
        }
        // else (basic → pro, or same-tier re-click): let the <a> open Onvo normally
    };

    const confirmUpgradeRedirect = () => {
        if (pendingUpgradeHref) {
            window.open(pendingUpgradeHref, "_blank", "noopener,noreferrer");
        }
        setUpgradeWarningOpen(false);
        setUpgradeOpen(false);
    };

    const handleCancelSubscription = async () => {
        setCancelLoading(true);
        try {
            const { data } = await api.post("/v1/subscriptions/cancel");
            toast.success(
                `Tu plan se cancelará el ${fmtSubscriptionDate(data.access_until)}. Hasta entonces seguís con acceso completo.`
            );
            setCancelOpen(false);
            await loadStats();
            await checkAuth();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "object" ? detail?.message : detail;
            toast.error(msg || "No pudimos cancelar tu plan. Probá de nuevo en un momento.");
        } finally {
            setCancelLoading(false);
        }
    };

    const handleReactivateSubscription = async () => {
        try {
            await api.post("/v1/subscriptions/reactivate");
            toast.success("Tu plan Pyme Pro sigue activo.");
            await loadStats();
            await checkAuth();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "object" ? detail?.message : detail;
            toast.error(msg || "No pudimos reactivar tu plan.");
        }
    };

    // Realtime WebSocket: prepend new tx + play cash-register sound
    useRealtimeTx({
        enabled: !!user,
        onTransaction: (newTx) => {
            setTxs((prev) => {
                if (prev.some((t) => t.transaction_id === newTx.transaction_id)) return prev;
                return [newTx, ...prev];
            });
            playCashRegister();
            toast.success(
                `Nuevo pago · ${newTx.reference || "sin ref"} · ${typeof newTx.amount === "number" ? newTx.amount.toLocaleString("es-CR") : ""}`
            );
            loadStats();
        },
    });

    // Sync settings state when user data updates
    useEffect(() => {
        if (user) {
            setPhone(user.phone_number || "");
            setBusinessName(user.business_name || "");
            setSlug(user.slug || "");
            setApiKey(user.cybersinpe_api_key || "");
            setNotifyEmail(user.notify_email ?? true);
            setNotifySms(user.notify_sms ?? false);
        }
    }, [user]);

    // Open settings automatically if no phone yet — ONLY for owners
    useEffect(() => {
        if (user && user.role !== "staff" && !user.phone_number) {
            setSettingsOpen(true);
        }
    }, [user]);

    const saveSettings = async () => {
        if (!phone.trim()) {
            toast.error("Ingresá tu número SINPE");
            return;
        }
        const slugClean = slug.trim().toLowerCase();
        if (slugClean && !/^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$/.test(slugClean)) {
            toast.error("Slug inválido. Solo minúsculas, números y guiones (3-40 caracteres).");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.patch("/users/profile", {
                phone_number: phone.trim(),
                business_name: businessName.trim(),
                slug: slugClean,
                cybersinpe_api_key: apiKey.trim(),
                notify_email: notifyEmail,
                notify_sms: notifySms,
            });
            setUser(data);
            toast.success("Configuración guardada");
            setSettingsOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No pudimos guardar los cambios");
        } finally {
            setSaving(false);
        }
    };

    const copyLink = async () => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(paymentLink);
            } else {
                const ta = document.createElement("textarea");
                ta.value = paymentLink;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            toast.success("Link copiado al portapapeles");
        } catch (e) {
            toast.error("No pudimos copiar. Copialo manualmente.");
        }
    };

    const statusLabel = (s) =>
        s === "verified"
            ? "Verificado"
            : s === "failed"
            ? "Rechazado"
            : s === "manual_review"
            ? "Pendiente de revisión"
            : "Pendiente";

    const manualReview = async (tx, action) => {
        setReviewingId(tx.transaction_id);
        try {
            const { data } = await api.patch(`/transactions/${tx.transaction_id}/manual-verify`, { action });
            setTxs((prev) => prev.map((t) => (t.transaction_id === data.transaction_id ? data : t)));
            toast.success(action === "verified" ? "Pago marcado como verificado" : "Pago marcado como rechazado");
            // Refresh stats in background
            api.get("/transactions/stats").then(({ data }) => setStats(data)).catch(() => {});
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo actualizar la transacción");
        } finally {
            setReviewingId(null);
        }
    };

    const triggerUpload = (tx) => {
        pendingUploadTxRef.current = tx;
        fileInputRef.current?.click();
    };

    const onFileChosen = async (e) => {
        const file = e.target.files?.[0];
        const tx = pendingUploadTxRef.current;
        e.target.value = ""; // reset so the same file can be re-picked
        pendingUploadTxRef.current = null;
        if (!file || !tx) return;

        if (!file.type.startsWith("image/")) {
            toast.error("Subí una imagen (JPG, PNG, WEBP o HEIC).");
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error("La imagen no debe superar 5 MB.");
            return;
        }

        setUploadingId(tx.transaction_id);
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post(
                `/transactions/${tx.transaction_id}/comprobante`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } }
            );
            setTxs((prev) => prev.map((t) => (t.transaction_id === data.transaction_id ? data : t)));
            if (data.is_duplicate) {
                toast.error("DUPLICADO: ya existe una transacción con esa referencia para tu cuenta.", { duration: 6000 });
            } else if (data.ai_extraction?.confidence) {
                toast.success(`Comprobante validado por IA (${Math.round(data.ai_extraction.confidence * 100)}% confianza)`);
            } else {
                toast.success("Comprobante guardado");
            }
        } catch (err) {
            const detail = err?.response?.data?.detail;
            // Backend returns 422 with structured detail when AI rejects the image
            if (detail && typeof detail === "object" && detail.code === "not_a_receipt") {
                toast.error(detail.message || "La imagen no es un comprobante.", { duration: 6000 });
            } else {
                toast.error(typeof detail === "string" ? detail : (detail?.message || "No se pudo subir la imagen"));
            }
        } finally {
            setUploadingId(null);
        }
    };

    const comprobanteSrcFor = (tx) =>
        `${API}/transactions/${tx.transaction_id}/comprobante?v=${encodeURIComponent(tx.comprobante_uploaded_at || "")}`;

    const openEdit = (tx) => {
        setEditingTx(tx);
        setEditForm({
            amount: String(tx.amount ?? ""),
            reference: tx.reference || "",
            customer_name: tx.customer_name || "",
            numero_factura: tx.numero_factura || "",
        });
    };

    const saveEdit = async () => {
        if (!editingTx) return;
        const amt = parseFloat(editForm.amount);
        if (!amt || amt <= 0) {
            toast.error("Ingresá un monto válido");
            return;
        }
        if (!editForm.reference.trim() || editForm.reference.trim().length < 3) {
            toast.error("Ingresá una referencia válida");
            return;
        }
        setSavingEdit(true);
        try {
            const { data } = await api.patch(`/transactions/${editingTx.transaction_id}`, {
                amount: amt,
                reference: editForm.reference.trim(),
                customer_name: editForm.customer_name.trim(),
                numero_factura: editForm.numero_factura.trim(),
            });
            setTxs((prev) => prev.map((t) => (t.transaction_id === data.transaction_id ? data : t)));
            toast.success("Transacción actualizada");
            setEditingTx(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo guardar");
        } finally {
            setSavingEdit(false);
        }
    };

    const onPillClick = (value) => {
        setDateRange(value);
        setStartDate(null);
        setEndDate(null);
    };

    const onPickStart = (date) => {
        setStartDate(date);
        setDateRange("all");
    };

    const onPickEnd = (date) => {
        setEndDate(date);
        setDateRange("all");
    };

    const clearDates = () => {
        setStartDate(null);
        setEndDate(null);
    };

    const exportCSV = () => {
        if (!txs.length) {
            toast.info("No hay transacciones para exportar");
            return;
        }
        const esc = (v) => {
            const s = v === null || v === undefined ? "" : String(v);
            return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const headers = ["Fecha", "Referencia", "Núm. factura", "Cliente", "Monto (CRC)", "Estado", "Comprobante URL"];
        const rows = txs.map((t) => [
            new Date(t.created_at).toISOString(),
            t.reference,
            t.numero_factura || "",
            t.customer_name || "",
            Number(t.amount).toFixed(2),
            statusLabel(t.status),
            t.comprobante_url ? `${window.location.origin}${API.replace(window.location.origin, "")}/transactions/${t.transaction_id}/comprobante` : "",
        ]);
        const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n");
        const bom = "\ufeff"; // Excel UTF-8 friendly
        const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        const date = new Date().toISOString().slice(0, 10);
        a.href = url;
        a.download = `sinpe-flow-historial-${date}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`${txs.length} transacciones exportadas`);
    };

    const downloadQR = () => {
        const canvas = qrContainerRef.current?.querySelector("canvas");
        if (!canvas) {
            toast.error("No se pudo generar el QR");
            return;
        }
        const label = (user?.business_name || user?.name || "sinpe-flow")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "");
        canvas.toBlob((blob) => {
            if (!blob) {
                toast.error("No se pudo generar el archivo");
                return;
            }
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `qr-pago-${label || "sinpe-flow"}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("QR descargado");
        }, "image/png");
    };

    const stopImpersonating = async () => {
        try {
            await api.post("/admin/stop-impersonating");
            const me = await api.get("/auth/me");
            setUser(me.data);
            toast.success("Volviste al panel admin");
            navigate("/admin-portal", { replace: true });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo volver");
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC]" data-testid="dashboard-page">
            <OnboardingBanner />
            {user?.impersonation && (
                <div className="bg-[#064E3B] text-white" data-testid="impersonation-banner">
                    <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-3 text-sm">
                            <div className="h-8 w-8 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
                                <Shield className="w-4 h-4" />
                            </div>
                            <div>
                                <span className="font-medium">Modo impersonación</span>
                                <span className="text-white/80"> · Estás viendo SINPE-Flow como <strong>{user.name}</strong>. Iniciada por {user.impersonation.admin_email}.</span>
                            </div>
                        </div>
                        <button
                            onClick={stopImpersonating}
                            className="inline-flex items-center gap-2 bg-white text-[#064E3B] rounded-full px-5 py-2 text-sm font-medium hover:bg-[#D1FAE5] transition-colors whitespace-nowrap"
                            data-testid="stop-impersonating-button"
                        >
                            <LogIn className="w-3.5 h-3.5 rotate-180" /> Volver al admin
                        </button>
                    </div>
                </div>
            )}
            {stats?.monthly_limit_reached && (
                <div className="bg-amber-50 border-b border-amber-200" data-testid="limit-banner">
                    <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-3 text-sm text-amber-900">
                            <div className="h-8 w-8 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
                                <AlertTriangle className="w-4 h-4" />
                            </div>
                            <div>
                                <span className="font-medium">
                                    {stats.plan === "pro"
                                        ? `Llegaste al límite de ${TIER_LABEL[stats.subscription_tier] || "tu plan"}`
                                        : "Llegaste al límite del Plan Prueba"}
                                </span>
                                <span className="text-amber-800"> · {stats.monthly_tx_count}/{stats.monthly_limit} transacciones este mes. {stats.plan === "pro" ? "Comunicate con soporte para gestionar tu cuenta." : "No podés recibir más hasta el próximo mes o hasta que mejores a Pyme Pro."}</span>
                            </div>
                        </div>
                        {stats.plan === "pro" ? (
                            <a
                                href={WHATSAPP_UPGRADE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 bg-[#064E3B] text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-[#042F2E] transition-colors whitespace-nowrap"
                                data-testid="banner-support-cta"
                            >
                                Contactar soporte
                            </a>
                        ) : (
                            <button
                                onClick={() => setUpgradeOpen(true)}
                                className="inline-flex items-center gap-2 bg-[#064E3B] text-white rounded-full px-5 py-2 text-sm font-medium hover:bg-[#042F2E] transition-colors whitespace-nowrap"
                                data-testid="banner-upgrade-cta"
                            >
                                <Crown className="w-3.5 h-3.5" /> Mejorar a Pyme Pro
                            </button>
                        )}
                    </div>
                </div>
            )}
            <Header
                rightSlot={
                    <>
                        {user?.is_admin && (
                            <Link
                                to="/admin-portal"
                                className="btn-ghost flex items-center gap-2 text-[#064E3B] font-medium"
                                data-testid="open-admin-portal"
                            >
                                <Shield className="w-4 h-4" /> Admin
                            </Link>
                        )}
                        {!isStaff && (
                            <button
                                onClick={() => setSettingsOpen(true)}
                                className="btn-ghost flex items-center gap-2"
                                data-testid="open-settings-button"
                            >
                                <Settings className="w-4 h-4" /> Ajustes
                            </button>
                        )}
                        {isStaff && (
                            <span
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-100 text-violet-800 text-xs font-medium"
                                data-testid="staff-badge"
                            >
                                <UserIcon className="w-3 h-3" /> Empleado
                            </span>
                        )}
                        <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-full bg-white border border-[#E2E8F0]">
                            {user?.picture ? (
                                <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                            ) : (
                                <div className="w-7 h-7 rounded-full bg-[#064E3B] flex items-center justify-center text-white text-xs font-medium">
                                    {user?.name?.[0] || "U"}
                                </div>
                            )}
                            <span className="text-sm font-medium text-[#022C22] hidden sm:inline">{user?.name}</span>
                        </div>
                        <button onClick={logout} className="btn-ghost" data-testid="logout-button" title="Cerrar sesión">
                            <LogOut className="w-4 h-4" />
                        </button>
                    </>
                }
            />

            <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                >
                    <p className="text-sm uppercase tracking-widest text-[#10B981] font-medium mb-2">Tu dashboard</p>
                    <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#022C22] tracking-tight">
                        Hola, {user?.name?.split(" ")[0] || "vendedor"}.
                    </h1>
                    <p className="text-[#334155] mt-3 text-lg">
                        Aquí está el resumen de tus pagos verificados.
                    </p>
                </motion.div>

                {/* Payment link card — owner only (staff can't share or copy links) */}
                {!isStaff && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.05 }}
                    className="mt-8 card-clean p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-5 md:gap-6 bg-gradient-to-br from-white to-[#F0FDF4]"
                    data-testid="payment-link-card"
                >
                    <div className="h-12 w-12 rounded-2xl bg-[#064E3B] flex items-center justify-center flex-shrink-0">
                        <LinkIcon className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs uppercase tracking-widest text-[#64748B]">Tu link de pago</p>
                        <p className="font-mono text-sm md:text-base text-[#022C22] mt-1 truncate" data-testid="payment-link-value">
                            {paymentLink}
                        </p>
                    </div>
                    <button onClick={() => setQrOpen(true)} className="btn-secondary text-sm" data-testid="open-qr-button">
                        <QrCode className="w-4 h-4" /> Ver QR
                    </button>
                    <button onClick={copyLink} className="btn-primary text-sm" data-testid="copy-link-button">
                        <Copy className="w-4 h-4" /> Copiar link
                    </button>
                </motion.div>
                )}

                {/* Stats — staff sees only "Verificados hoy" and "Pendientes" (no ganancias, no plan) */}
                <div className={`mt-8 grid grid-cols-1 sm:grid-cols-2 ${isStaff ? "lg:grid-cols-2" : "lg:grid-cols-4"} gap-5`}>
                    <StatCard icon={CheckCircle2} label="Verificados hoy" value={stats.verified_today} accent="bg-[#D1FAE5] text-[#065F46]" />
                    {!isStaff && (
                        <StatCard icon={Wallet} label="Monto total" value={formatCRC(stats.total_amount)} accent="bg-[#064E3B]/5 text-[#064E3B]" />
                    )}
                    <StatCard icon={Clock} label="Pendientes" value={stats.pending_count} accent="bg-[#FEF3C7] text-[#92400E]" />
                    {!isStaff && (
                    <div className="card-clean p-6 flex flex-col gap-3" data-testid="stat-plan-usage">
                        <div className="flex items-center justify-between">
                            <p className="text-xs uppercase tracking-widest text-[#64748B]">
                                {stats.plan === "pro"
                                    ? (TIER_LABEL[stats.subscription_tier] || "Plan Pyme Pro")
                                    : "Uso del Plan Prueba"}
                            </p>
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${stats.plan === "pro" ? "bg-[#064E3B] text-white" : "bg-amber-100 text-amber-700"}`}>
                                <Crown className="w-5 h-5" strokeWidth={1.75} />
                            </div>
                        </div>
                        {stats.monthly_limit ? (
                            // Both basic AND pro tiers now have a numeric cap (20 / 300 / 1200)
                            <>
                                <p className="font-display text-2xl md:text-3xl font-semibold text-[#022C22] tracking-tight" data-testid="plan-usage-count">
                                    {stats.monthly_tx_count}<span className="text-base text-[#64748B] font-normal">/{stats.monthly_limit}</span>
                                </p>
                                <div
                                    className="h-2 rounded-full bg-[#F1F5F9] overflow-hidden"
                                    role="progressbar"
                                    aria-valuenow={stats.monthly_tx_count}
                                    aria-valuemin={0}
                                    aria-valuemax={stats.monthly_limit}
                                    data-testid="plan-usage-progress"
                                >
                                    <div
                                        className={`h-full rounded-full transition-all ${stats.monthly_limit_reached ? "bg-red-500" : (stats.monthly_tx_count / stats.monthly_limit) > 0.8 ? "bg-amber-500" : "bg-[#10B981]"}`}
                                        style={{ width: `${Math.min(100, (stats.monthly_tx_count / Math.max(1, stats.monthly_limit)) * 100)}%` }}
                                    />
                                </div>
                                {stats.next_cycle_at && (
                                    <div
                                        className="rounded-lg bg-[#F0FDF4] border border-[#10B981]/30 px-3 py-2 text-[11px] text-[#065F46] leading-snug"
                                        data-testid="cycle-banner"
                                    >
                                        <span className="font-medium">Tu ciclo se renueva el {fmtCycleDate(stats.next_cycle_at)}</span>
                                        {daysUntil(stats.next_cycle_at) !== null && (
                                            <> · faltan {daysUntil(stats.next_cycle_at)} día{daysUntil(stats.next_cycle_at) === 1 ? "" : "s"}</>
                                        )}
                                        <> · llevás {stats.monthly_tx_count}/{stats.monthly_limit}</>
                                    </div>
                                )}
                                {stats.plan === "pro" && stats.subscription_expires_at && stats.subscription_status === "scheduled_cancel" ? (
                                    <div
                                        className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 leading-snug"
                                        data-testid="subscription-cancelled-banner"
                                    >
                                        <p className="font-medium">Tu Pyme Pro se cancela el {fmtSubscriptionDate(stats.subscription_expires_at)}</p>
                                        <p className="mt-0.5 text-amber-800">Hasta entonces seguís con acceso completo.</p>
                                        <button
                                            onClick={handleReactivateSubscription}
                                            className="mt-1.5 text-xs font-medium text-amber-900 underline hover:no-underline"
                                            data-testid="reactivate-subscription-link"
                                        >
                                            Reactivar mi plan
                                        </button>
                                    </div>
                                ) : (
                                    stats.plan === "pro" && stats.subscription_expires_at && (
                                        <p className="text-[11px] text-[#64748B]" data-testid="subscription-expires-at">
                                            Pyme Pro renueva el {fmtSubscriptionDate(stats.subscription_expires_at)}
                                        </p>
                                    )
                                )}
                                {stats.plan === "basic" && (
                                    <button
                                        onClick={() => setUpgradeOpen(true)}
                                        className="text-xs font-medium text-[#064E3B] hover:underline text-left"
                                        data-testid="open-upgrade-modal"
                                    >
                                        Mejorar a Pyme Pro →
                                    </button>
                                )}
                                {stats.plan === "pro" && stats.subscription_status === "active" && stats.subscription_expires_at && (
                                    <div className="flex gap-3 text-xs">
                                        <button
                                            onClick={() => setUpgradeOpen(true)}
                                            className="font-medium text-[#064E3B] hover:underline"
                                            data-testid="open-upgrade-modal-pro"
                                        >
                                            Cambiar plan →
                                        </button>
                                        <button
                                            onClick={() => setCancelOpen(true)}
                                            className="font-medium text-[#94A3B8] hover:text-[#64748B] hover:underline"
                                            data-testid="open-cancel-modal"
                                        >
                                            Cancelar suscripción
                                        </button>
                                    </div>
                                )}
                            </>
                        ) : (
                            // Legacy pro user without explicit cap → unlimited (compat)
                            <>
                                <p className="font-display text-2xl md:text-3xl font-semibold text-[#022C22] tracking-tight">Ilimitado</p>
                                <p className="text-xs text-[#64748B]">{stats.monthly_tx_count} transacciones este mes</p>
                                {stats.subscription_expires_at && (
                                    <p className="text-[11px] text-[#64748B]" data-testid="subscription-expires-at">
                                        Renueva el {fmtSubscriptionDate(stats.subscription_expires_at)}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                    )}
                </div>

                {/* Main content — split in tabs for owners; staff sees only the table */}
                {isStaff ? (
                    <div className="mt-6">{/* staff: tabla suelta */}</div>
                ) : (
                    <Tabs defaultValue="transacciones" className="mt-8" data-testid="dashboard-tabs">
                        <TabsList className="grid w-full max-w-md grid-cols-2" data-testid="dashboard-tabs-list">
                            <TabsTrigger value="transacciones" data-testid="tab-transacciones">Transacciones</TabsTrigger>
                            <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
                        </TabsList>
                        <TabsContent value="personal" className="mt-6 space-y-6" data-testid="tab-content-personal">
                            <StaffManager ownerPlan={user?.plan} />
                        </TabsContent>
                        <TabsContent value="transacciones" className="mt-6">
                            {/* Sales chart */}
                            <div>
                                <SalesChart transactions={txs} dateRange={dateRange} />
                            </div>
                        </TabsContent>
                    </Tabs>
                )}

                {/* Transactions */}
                <div className="mt-6 card-clean overflow-hidden" data-testid="transactions-card">
                    <div className="p-5 md:p-6 border-b border-[#E2E8F0] flex flex-col gap-5">
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                            <div className="flex-1">
                                <h2 className="font-display text-xl md:text-2xl font-medium text-[#022C22]">Pagos verificados</h2>
                                <p className="text-sm text-[#64748B] mt-1">
                                    Historial filtrable de todas tus transacciones.
                                </p>
                            </div>
                            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-[#F8FAFC] border border-[#E2E8F0] self-start md:self-auto" data-testid="date-range-pills">
                                {[
                                    { value: "all", label: "Todo" },
                                    { value: "today", label: "Hoy" },
                                    { value: "week", label: "Semana" },
                                    { value: "month", label: "Mes" },
                                ].map((opt) => {
                                    const isActive = !startDate && !endDate && dateRange === opt.value;
                                    return (
                                        <button
                                            key={opt.value}
                                            onClick={() => onPillClick(opt.value)}
                                            className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
                                                isActive
                                                    ? "bg-[#064E3B] text-white shadow-[0_2px_8px_rgba(6,78,59,0.2)]"
                                                    : "text-[#64748B] hover:text-[#022C22]"
                                            }`}
                                            data-testid={`date-pill-${opt.value}`}
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Exact date range pickers */}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-testid="date-range-picker">
                            <span className="text-xs uppercase tracking-widest text-[#64748B] font-medium">Rango exacto</span>
                            <div className="flex flex-1 items-center gap-2 flex-wrap">
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button
                                            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] text-sm text-[#022C22] hover:bg-white transition-colors min-w-[180px]"
                                            data-testid="date-start-trigger"
                                        >
                                            <CalendarIcon className="w-4 h-4 text-[#64748B]" />
                                            <span className={startDate ? "" : "text-[#64748B]"}>
                                                {startDate ? `Desde · ${fmtShort(startDate)}` : "Desde"}
                                            </span>
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0 rounded-2xl" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={startDate}
                                            onSelect={onPickStart}
                                            disabled={(d) => (endDate ? d > endDate : false)}
                                            data-testid="date-start-calendar"
                                        />
                                    </PopoverContent>
                                </Popover>

                                <span className="text-[#64748B] text-sm hidden sm:inline">→</span>

                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button
                                            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] text-sm text-[#022C22] hover:bg-white transition-colors min-w-[180px]"
                                            data-testid="date-end-trigger"
                                        >
                                            <CalendarIcon className="w-4 h-4 text-[#64748B]" />
                                            <span className={endDate ? "" : "text-[#64748B]"}>
                                                {endDate ? `Hasta · ${fmtShort(endDate)}` : "Hasta"}
                                            </span>
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0 rounded-2xl" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={endDate}
                                            onSelect={onPickEnd}
                                            disabled={(d) => (startDate ? d < startDate : false) || d > new Date()}
                                            data-testid="date-end-calendar"
                                        />
                                    </PopoverContent>
                                </Popover>

                                {(startDate || endDate) && (
                                    <button
                                        onClick={clearDates}
                                        className="inline-flex items-center gap-1.5 text-xs text-[#991B1B] hover:bg-[#FEE2E2] px-2.5 py-1.5 rounded-full transition-colors"
                                        data-testid="clear-dates-button"
                                    >
                                        <X className="w-3 h-3" /> Limpiar rango
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row md:items-center gap-3">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                                <input
                                    placeholder="Buscar referencia o cliente"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="input-clean pl-10"
                                    data-testid="search-input"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <Filter className="w-4 h-4 text-[#64748B]" />
                                <Select value={filterStatus} onValueChange={setFilterStatus}>
                                    <SelectTrigger className="w-[160px] rounded-xl border-[#E2E8F0] bg-[#F8FAFC]" data-testid="status-filter">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all" data-testid="filter-all">Todos</SelectItem>
                                        <SelectItem value="verified" data-testid="filter-verified">Verificados</SelectItem>
                                        <SelectItem value="pending" data-testid="filter-pending">Pendientes</SelectItem>
                                        <SelectItem value="manual_review" data-testid="filter-manual-review">Por revisar</SelectItem>
                                        <SelectItem value="failed" data-testid="filter-failed">Rechazados</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            {!isStaff && (
                                <button
                                    onClick={exportCSV}
                                    className="btn-secondary text-sm whitespace-nowrap"
                                    data-testid="export-csv-button"
                                    title="Exportar historial visible a CSV"
                                >
                                    <Download className="w-4 h-4" /> Exportar historial
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="transactions-table">
                            <thead>
                                <tr className="text-left border-b border-[#E2E8F0] bg-[#F8FAFC]">
                                    <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs">Fecha</th>
                                    <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs">Referencia</th>
                                    <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs">Núm. factura</th>
                                    <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs">Cliente</th>
                                    <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs text-right">Monto</th>
                                    <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs text-right">Estado</th>
                                    <th className="px-5 md:px-6 py-4 font-medium text-[#64748B] uppercase tracking-wider text-xs text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loadingTxs ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-16 text-center text-[#64748B]" data-testid="loading-txs">
                                            <div className="inline-block h-6 w-6 rounded-full border-2 border-[#064E3B] border-t-transparent animate-spin" />
                                        </td>
                                    </tr>
                                ) : txs.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-20 text-center" data-testid="empty-state">
                                            <div className="inline-flex flex-col items-center gap-3">
                                                <div className="h-14 w-14 rounded-full bg-[#F8FAFC] flex items-center justify-center">
                                                    <Wallet className="w-6 h-6 text-[#64748B]" />
                                                </div>
                                                <p className="font-medium text-[#022C22]">Aún no hay pagos registrados</p>
                                                <p className="text-sm text-[#64748B] max-w-sm">
                                                    Comparte tu link de pago con tus clientes para empezar a recibir transacciones verificadas.
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    txs.map((t) => (
                                        <tr key={t.transaction_id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors" data-testid={`tx-row-${t.transaction_id}`}>
                                            <td className="px-5 md:px-6 py-4 text-[#334155]">{formatDate(t.created_at)}</td>
                                            <td className="px-5 md:px-6 py-4 font-mono text-[#022C22]">{t.reference}</td>
                                            <td className="px-5 md:px-6 py-4 font-mono text-[#334155]">
                                                {t.numero_factura ? (
                                                    <span data-testid={`factura-${t.transaction_id}`}>{t.numero_factura}</span>
                                                ) : (
                                                    <span className="text-[#94A3B8]">—</span>
                                                )}
                                            </td>
                                            <td className="px-5 md:px-6 py-4 text-[#334155]">
                                                <div className="flex items-center gap-2">
                                                    <UserIcon className="w-3.5 h-3.5 text-[#64748B]" />
                                                    {t.customer_name || "—"}
                                                </div>
                                            </td>
                                            <td className="px-5 md:px-6 py-4 text-right font-mono font-medium text-[#022C22]">{formatCRC(t.amount)}</td>
                                            <td className="px-5 md:px-6 py-4 text-right">
                                                <div className="inline-flex flex-col items-end gap-1">
                                                    <StatusBadge status={t.status} />
                                                    {t.is_duplicate && (
                                                        <span
                                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 border border-red-300 text-red-800 text-[10px] font-semibold uppercase tracking-wider"
                                                            data-testid={`duplicate-badge-${t.transaction_id}`}
                                                            title="Esta referencia ya existe en otra transacción tuya"
                                                        >
                                                            <AlertTriangle className="w-2.5 h-2.5" /> Duplicado
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-5 md:px-6 py-4 text-right">
                                                <div className="inline-flex items-center justify-end gap-1.5" data-testid={`actions-${t.transaction_id}`}>
                                                    {t.comprobante_url && (
                                                        <button
                                                            onClick={() => setComprobanteOpen(t)}
                                                            className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#F0FDF4] text-[#065F46] hover:bg-[#D1FAE5] transition-colors"
                                                            data-testid={`view-comprobante-${t.transaction_id}`}
                                                            title="Ver comprobante adjunto"
                                                        >
                                                            <Paperclip className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                    {t.status === "manual_review" && (
                                                        <>
                                                            <button
                                                                onClick={() => openEdit(t)}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#F8FAFC] border border-[#E2E8F0] text-[#022C22] text-xs font-medium hover:bg-[#064E3B] hover:text-white hover:border-[#064E3B] transition-colors"
                                                                data-testid={`edit-tx-${t.transaction_id}`}
                                                                title="Editar transacción"
                                                            >
                                                                <Pencil className="w-3 h-3" /> Editar
                                                            </button>
                                                            <button
                                                                onClick={() => triggerUpload(t)}
                                                                disabled={uploadingId === t.transaction_id}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#F8FAFC] border border-[#E2E8F0] text-[#022C22] text-xs font-medium hover:bg-[#064E3B] hover:text-white hover:border-[#064E3B] transition-colors disabled:opacity-50"
                                                                data-testid={`upload-comprobante-${t.transaction_id}`}
                                                                title={t.comprobante_url ? "Reemplazar comprobante" : "Subir captura del SINPE"}
                                                            >
                                                                {uploadingId === t.transaction_id ? (
                                                                    <span className="h-3 w-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
                                                                ) : (
                                                                    <Upload className="w-3 h-3" />
                                                                )}
                                                                {t.comprobante_url ? "Reemplazar" : "Subir"}
                                                            </button>
                                                            <button
                                                                onClick={() => manualReview(t, "verified")}
                                                                disabled={reviewingId === t.transaction_id}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#D1FAE5] text-[#065F46] text-xs font-medium hover:bg-[#A7F3D0] transition-colors disabled:opacity-50"
                                                                data-testid={`manual-verify-${t.transaction_id}`}
                                                                title="Marcar como verificado"
                                                            >
                                                                <CheckCircle2 className="w-3 h-3" /> Verificar
                                                            </button>
                                                            <button
                                                                onClick={() => manualReview(t, "failed")}
                                                                disabled={reviewingId === t.transaction_id}
                                                                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-[#FEE2E2] text-[#991B1B] text-xs font-medium hover:bg-[#FECACA] transition-colors disabled:opacity-50"
                                                                data-testid={`manual-reject-${t.transaction_id}`}
                                                                title="Marcar como rechazado"
                                                            >
                                                                <XCircle className="w-3 h-3" /> Rechazar
                                                            </button>
                                                        </>
                                                    )}
                                                    {t.status !== "manual_review" && !t.comprobante_url && (
                                                        <span className="text-xs text-[#94A3B8]">—</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>

            {/* Settings dialog */}
            <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
                <DialogContent className="rounded-2xl max-w-md" data-testid="settings-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl font-semibold text-[#022C22]">
                            Tu cuenta SINPE
                        </DialogTitle>
                        <DialogDescription className="text-[#334155]">
                            Configura tu número de teléfono y cómo quieres recibir las notificaciones.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-5 py-2 max-h-[60vh] overflow-y-auto pr-1">
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-[#022C22]">Nombre del negocio</Label>
                            <div className="relative">
                                <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                                <input
                                    value={businessName}
                                    onChange={(e) => setBusinessName(e.target.value)}
                                    placeholder="Tienda Pura Vida"
                                    className="input-clean pl-10"
                                    data-testid="business-name-input"
                                />
                            </div>
                            <p className="text-xs text-[#64748B]">Se mostrará en grande en la página de pago de tus clientes.</p>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-[#022C22]">Slug personalizado del link</Label>
                            <div className="flex items-stretch rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] focus-within:ring-2 focus-within:ring-[#10B981] focus-within:bg-white overflow-hidden">
                                <span className="px-3 flex items-center text-sm text-[#64748B] font-mono border-r border-[#E2E8F0] bg-white">
                                    /pay/
                                </span>
                                <input
                                    value={slug}
                                    onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                                    placeholder="mi-negocio"
                                    className="flex-1 bg-transparent px-3 py-3 text-sm font-mono text-[#022C22] outline-none"
                                    data-testid="slug-input"
                                    maxLength={40}
                                />
                            </div>
                            <p className="text-xs text-[#64748B]">Solo minúsculas, números y guiones. Ejemplo: <span className="font-mono">tienda-pura-vida</span></p>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-[#022C22]">Número SINPE Móvil</Label>
                            <input
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="8888-1234"
                                className="input-clean"
                                data-testid="phone-input"
                            />
                            <p className="text-xs text-[#64748B]">
                                Es el número al que tus clientes harán SINPE Móvil.{" "}
                                <strong className="text-[#064E3B]">SINPE-Flow lo lee de cada comprobante</strong>{" "}
                                que llega por WhatsApp para enrutar el pago a tu cuenta automáticamente.
                            </p>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-[#022C22]">CyberSINPE API Key</Label>
                            <div className="relative">
                                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                                <input
                                    type="password"
                                    value={apiKey}
                                    onChange={(e) => setApiKey(e.target.value)}
                                    placeholder="sk_live_..."
                                    className="input-clean pl-10 font-mono"
                                    data-testid="api-key-input"
                                    autoComplete="off"
                                />
                            </div>
                            <div className={`flex items-start gap-2 text-xs rounded-lg p-2.5 ${apiKey.trim() ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEF3C7] text-[#92400E]"}`} data-testid="api-key-mode-indicator">
                                {apiKey.trim() ? (
                                    <>
                                        <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                        <span><strong>Modo automático activo.</strong> Los pagos se validan contra el banco al instante.</span>
                                    </>
                                ) : (
                                    <>
                                        <Eye className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                        <span><strong>Modo revisión manual.</strong> Los pagos llegan como "Pendiente de revisión" y vos los validás desde el dashboard.</span>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center justify-between rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                            <div>
                                <Label className="text-sm font-medium text-[#022C22]">Notificación por email</Label>
                                <p className="text-xs text-[#64748B] mt-0.5">Recibe un email al verificar un pago</p>
                            </div>
                            <Switch
                                checked={notifyEmail}
                                onCheckedChange={setNotifyEmail}
                                data-testid="notify-email-switch"
                            />
                        </div>

                        <div className="flex items-center justify-between rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] p-4">
                            <div>
                                <Label className="text-sm font-medium text-[#022C22]">Notificación por SMS</Label>
                                <p className="text-xs text-[#64748B] mt-0.5">Recibe un SMS al verificar un pago</p>
                            </div>
                            <Switch
                                checked={notifySms}
                                onCheckedChange={setNotifySms}
                                data-testid="notify-sms-switch"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <button
                            onClick={() => setSettingsOpen(false)}
                            className="btn-ghost"
                            data-testid="cancel-settings-button"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={saveSettings}
                            disabled={saving}
                            className="btn-primary"
                            data-testid="save-settings-button"
                        >
                            {saving ? "Guardando..." : "Guardar"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* QR Dialog */}
            <Dialog open={qrOpen} onOpenChange={setQrOpen}>
                <DialogContent className="rounded-2xl max-w-md" data-testid="qr-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl font-semibold text-[#022C22]">
                            Tu código QR de pago
                        </DialogTitle>
                        <DialogDescription className="text-[#334155]">
                            Imprimilo y pegalo en tu negocio. Tus clientes escanean y pagan al instante.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="py-4">
                        <div
                            ref={qrContainerRef}
                            className="mx-auto w-fit rounded-2xl bg-white border border-[#E2E8F0] p-6 flex flex-col items-center gap-4"
                            data-testid="qr-card"
                        >
                            <div className="flex items-center gap-2">
                                <div className="h-7 w-7 rounded-lg bg-[#064E3B] flex items-center justify-center">
                                    <LinkIcon className="w-3.5 h-3.5 text-white" />
                                </div>
                                <span className="font-display font-semibold text-[#022C22] text-sm">SINPE-Flow</span>
                            </div>

                            <QRCodeCanvas
                                value={paymentLink}
                                size={256}
                                level="H"
                                bgColor="#FFFFFF"
                                fgColor="#064E3B"
                                marginSize={2}
                                data-testid="qr-canvas"
                            />

                            <div className="text-center">
                                <p className="font-display text-lg font-semibold text-[#022C22] leading-tight">
                                    {user?.business_name || user?.name || ""}
                                </p>
                                <p className="text-xs text-[#64748B] mt-1 font-mono">
                                    /pay/{paymentIdentifier}
                                </p>
                            </div>
                        </div>
                        <p className="text-xs text-center text-[#64748B] mt-4">
                            Apuntá tu cámara y se abrirá la página de pago automáticamente.
                        </p>
                    </div>

                    <DialogFooter>
                        <button
                            onClick={() => setQrOpen(false)}
                            className="btn-ghost"
                            data-testid="close-qr-button"
                        >
                            Cerrar
                        </button>
                        <button
                            onClick={downloadQR}
                            className="btn-primary"
                            data-testid="download-qr-button"
                        >
                            <Download className="w-4 h-4" /> Descargar PNG
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit transaction dialog */}
            <Dialog open={!!editingTx} onOpenChange={(open) => !open && setEditingTx(null)}>
                <DialogContent className="rounded-2xl max-w-md" data-testid="edit-tx-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl font-semibold text-[#022C22]">
                            Editar transacción
                        </DialogTitle>
                        <DialogDescription className="text-[#334155]">
                            Solo podés editar transacciones <span className="font-medium text-[#92400E]">Pendientes de revisión</span>. Al marcar como verificada quedará bloqueada.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-5 py-2">
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-[#022C22]">Monto (₡)</Label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editForm.amount}
                                onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                                className="input-clean font-mono"
                                data-testid="edit-amount-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-[#022C22]">Referencia bancaria</Label>
                            <input
                                value={editForm.reference}
                                onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })}
                                placeholder="Ej. 928374651"
                                className="input-clean font-mono"
                                data-testid="edit-reference-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-[#022C22]">Tu nombre / Cliente</Label>
                            <input
                                value={editForm.customer_name}
                                onChange={(e) => setEditForm({ ...editForm, customer_name: e.target.value })}
                                placeholder="María Pérez"
                                className="input-clean"
                                data-testid="edit-customer-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-medium text-[#022C22]">Número de factura (opcional)</Label>
                            <div className="relative">
                                <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
                                <input
                                    value={editForm.numero_factura}
                                    onChange={(e) => setEditForm({ ...editForm, numero_factura: e.target.value })}
                                    placeholder="FACT-2026-001"
                                    className="input-clean pl-10 font-mono"
                                    data-testid="edit-factura-input"
                                />
                            </div>
                            <p className="text-xs text-[#64748B]">Para uso contable interno. Aparece en la tabla y en la exportación CSV.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <button
                            onClick={() => setEditingTx(null)}
                            className="btn-ghost"
                            data-testid="cancel-edit-button"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={saveEdit}
                            disabled={savingEdit}
                            className="btn-primary"
                            data-testid="save-edit-button"
                        >
                            {savingEdit ? "Guardando..." : "Guardar cambios"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Hidden file input for comprobante uploads */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                className="hidden"
                onChange={onFileChosen}
                data-testid="comprobante-file-input"
            />

            {/* Onvo Pay success welcome modal (triggered by ?payment=success) */}
            <Dialog open={paymentSuccessOpen} onOpenChange={setPaymentSuccessOpen}>
                <DialogContent className="rounded-2xl max-w-md" data-testid="payment-success-dialog">
                    <DialogHeader>
                        <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-[#10B981] to-[#064E3B] flex items-center justify-center mb-3 shadow-[0_8px_24px_rgba(16,185,129,0.35)]">
                            <Crown className="w-8 h-8 text-white" />
                        </div>
                        <DialogTitle
                            className="font-display text-2xl font-semibold text-[#022C22] text-center"
                            data-testid="payment-success-title"
                        >
                            {`¡Bienvenido a ${TIER_LABEL[stats?.subscription_tier] || "tu nuevo plan"}! 🎉`}
                        </DialogTitle>
                        <DialogDescription className="text-[#334155] text-center">
                            {stats?.subscription_tier === "pro_100"
                                ? "Tu Plan Emprendedor quedó activo. Ahora podés procesar hasta 100 transacciones al mes con alertas en tiempo real y exportar tus reportes contables."
                                : "Tu suscripción quedó activa. Ya podés procesar más transacciones, invitar empleados ilimitados y desbloquear todos los reportes contables."}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-2 py-3 px-2">
                        {(stats?.subscription_tier === "pro_100"
                            ? [
                                  "Tu nuevo límite de 100 tx/mes ya está activo",
                                  "Alertas de duplicados en tiempo real",
                                  "Dashboard en vivo con sonido de caja",
                                  "Exportación CSV para tu contador",
                              ]
                            : [
                                  "Tu nuevo límite de transacciones ya está activo",
                                  "Empleados ilimitados disponibles desde Personal",
                                  "Alertas de duplicados en tiempo real",
                                  "Reportes contables CSV código 06",
                              ]
                        ).map((f, i) => (
                            <div key={i} className="flex items-center gap-2.5 text-sm text-[#334155]">
                                <CheckCircle2 className="w-4 h-4 text-[#10B981] flex-shrink-0" /> {f}
                            </div>
                        ))}
                    </div>

                    <div className="rounded-xl bg-[#F0FDF4] border border-[#10B981]/30 p-3 mx-2 text-[12px] text-[#065F46] leading-relaxed">
                        💡 La activación final corre por cuenta del webhook de Onvo. Si en unos segundos no ves tu nuevo tier en el panel, refrescá la página.
                    </div>

                    <DialogFooter className="mt-2">
                        <button
                            onClick={() => setPaymentSuccessOpen(false)}
                            className="w-full inline-flex items-center justify-center gap-2 bg-[#064E3B] text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-[#042F2E] transition-colors"
                            data-testid="payment-success-close-button"
                        >
                            {`Empezar a usar ${TIER_LABEL[stats?.subscription_tier] || "mi plan"}`}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Upgrade dialog — picks pro_100 / pro_1 / pro_2 tier */}
            <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
                <DialogContent className="rounded-2xl max-w-3xl" data-testid="upgrade-dialog">
                    <DialogHeader>
                        <div className="mx-auto h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center mb-3">
                            <Crown className="w-7 h-7 text-amber-700" />
                        </div>
                        <DialogTitle className="font-display text-2xl font-semibold text-[#022C22] text-center">
                            Elegí tu plan
                        </DialogTitle>
                        <DialogDescription className="text-[#334155] text-center">
                            Desbloqueá más transacciones, empleados ilimitados, alertas en tiempo real y reportes contables. Suscripción mensual recurrente vía Onvo Pay.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 py-3">
                        {[
                            {
                                tier: "pro_100",
                                title: "Plan Emprendedor",
                                price: "₡5.000",
                                limit: "Hasta 100 transacciones/mes",
                                href: ONVO_CHECKOUT_PRO_100,
                                tag: "Microemprendedores",
                                highlight: false,
                            },
                            {
                                tier: "pro_1",
                                title: "Pyme Pro · Nivel 1",
                                price: "₡9.900",
                                limit: "Hasta 300 transacciones/mes",
                                href: ONVO_CHECKOUT_PRO_1,
                                tag: "Recomendado",
                                highlight: true,
                            },
                            {
                                tier: "pro_2",
                                title: "Pyme Pro · Nivel 2",
                                price: "₡16.900",
                                limit: "Hasta 1,200 transacciones/mes",
                                href: ONVO_CHECKOUT_PRO_2,
                                tag: "Volumen alto",
                                highlight: false,
                            },
                        ].map((p) => (
                            <div
                                key={p.tier}
                                className={`relative rounded-2xl p-5 flex flex-col gap-3 border-2 ${p.highlight ? "border-[#064E3B] bg-gradient-to-br from-white to-[#F0FDF4]" : "border-[#E2E8F0] bg-white"}`}
                                data-testid={`upgrade-card-${p.tier}`}
                            >
                                {p.highlight ? (
                                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full text-[10px] font-medium text-white bg-[#064E3B] whitespace-nowrap">
                                        {p.tag}
                                    </span>
                                ) : (
                                    <span className="text-[10px] uppercase tracking-widest font-medium text-[#94A3B8]">{p.tag}</span>
                                )}
                                <p className="font-display text-lg font-semibold text-[#022C22]">{p.title}</p>
                                <div className="flex items-baseline gap-1.5">
                                    <span className="font-display text-3xl font-semibold tracking-tighter text-[#022C22]">{p.price}</span>
                                    <span className="text-xs text-[#64748B]">/ mes</span>
                                </div>
                                <p className="text-xs text-[#475569]">{p.limit}</p>
                                <a
                                    href={p.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => handleUpgradeClick(e, p.tier, p.href, p.title)}
                                    className={`mt-auto inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${p.highlight ? "bg-[#064E3B] text-white hover:bg-[#042F2E]" : "bg-white border border-[#064E3B] text-[#064E3B] hover:bg-[#F0FDF4]"}`}
                                    data-testid={`upgrade-checkout-${p.tier}`}
                                >
                                    <Crown className="w-4 h-4" /> Suscribirme con Onvo
                                </a>
                            </div>
                        ))}
                    </div>

                    {/* Downgrade pro_2 → pro_1 has to go through support (no self-service yet) */}
                    {stats?.plan === "pro" && stats?.subscription_tier === "pro_2" && (
                        <div className="mt-2 px-2">
                            <a
                                href={WHATSAPP_UPGRADE_URL.replace("Pyme Pro", "Nivel 1 (downgrade)")}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block text-center text-[11px] text-[#64748B] hover:text-[#022C22] hover:underline"
                                data-testid="downgrade-contact-support"
                            >
                                ¿Querés bajar a Nivel 1? Contactá soporte →
                            </a>
                        </div>
                    )}

                    <p className="text-[11px] text-[#64748B] text-center px-2">
                        Pagos procesados de forma segura por Onvo Pay. Cancelás cuando quieras desde el portal del cliente.
                    </p>

                    <DialogFooter className="flex-col sm:flex-col gap-2 mt-2">
                        <a
                            href={WHATSAPP_UPGRADE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#064E3B] hover:underline text-center"
                            data-testid="upgrade-whatsapp-cta"
                        >
                            ¿Dudas? Escribinos por WhatsApp →
                        </a>
                        <button
                            onClick={() => setUpgradeOpen(false)}
                            className="text-xs text-[#64748B] hover:text-[#022C22] transition-colors"
                            data-testid="upgrade-close-button"
                        >
                            Tal vez más tarde
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Comprobante viewer dialog */}
            <Dialog open={!!comprobanteOpen} onOpenChange={(open) => !open && setComprobanteOpen(null)}>
                <DialogContent className="rounded-2xl max-w-2xl" data-testid="comprobante-viewer">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl font-semibold text-[#022C22]">
                            Comprobante adjunto
                        </DialogTitle>
                        <DialogDescription className="text-[#334155]">
                            {comprobanteOpen
                                ? `Ref ${comprobanteOpen.reference} · ${formatCRC(comprobanteOpen.amount)} · ${comprobanteOpen.customer_name || "Cliente"}`
                                : ""}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="rounded-2xl overflow-hidden bg-[#F8FAFC] border border-[#E2E8F0] flex items-center justify-center min-h-[300px]">
                        {comprobanteOpen && (
                            <img
                                src={comprobanteSrcFor(comprobanteOpen)}
                                alt="Comprobante SINPE"
                                className="max-h-[60vh] w-auto"
                                data-testid="comprobante-image"
                            />
                        )}
                    </div>
                    <DialogFooter className="gap-2">
                        {comprobanteOpen && (
                            <a
                                href={comprobanteSrcFor(comprobanteOpen)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-secondary text-sm"
                                data-testid="open-comprobante-newtab"
                            >
                                Abrir en nueva pestaña
                            </a>
                        )}
                        <button
                            onClick={() => setComprobanteOpen(null)}
                            className="btn-primary"
                            data-testid="close-comprobante-button"
                        >
                            Cerrar
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Upgrade double-charge warning — shown when a pro user tries to
                switch tier. Onvo Payment Links don't support automatic plan
                changes; the user MUST cancel their previous auto-charge in
                the Onvo customer portal. */}
            <Dialog open={upgradeWarningOpen} onOpenChange={setUpgradeWarningOpen}>
                <DialogContent className="rounded-2xl max-w-md" data-testid="upgrade-warning-dialog">
                    <DialogHeader>
                        <div className="mx-auto h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center mb-3">
                            <AlertTriangle className="w-7 h-7 text-amber-700" />
                        </div>
                        <DialogTitle className="font-display text-xl font-semibold text-[#022C22] text-center">
                            Atención: evitá un doble cobro
                        </DialogTitle>
                        <DialogDescription className="text-[#334155] text-center text-sm leading-relaxed">
                            Al confirmar el upgrade a <span className="font-semibold text-[#022C22]">{pendingUpgradeLabel}</span>, realizarás el pago del nuevo plan.{" "}
                            <span className="font-semibold">Recordá cancelar el auto-cobro de tu plan anterior en tu panel de Onvo</span> para evitar cargos duplicados el próximo mes.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-[12px] text-amber-900 leading-relaxed">
                        <p className="font-medium mb-1">Pasos recomendados:</p>
                        <ol className="list-decimal pl-4 space-y-1">
                            <li>Confirmá abajo para suscribirte al nuevo plan</li>
                            <li>Entrá a tu correo y buscá el recibo de Onvo Pay</li>
                            <li>Desde ahí cancelá el auto-cobro del plan anterior</li>
                        </ol>
                    </div>

                    <DialogFooter className="flex-col sm:flex-col gap-2 mt-2">
                        <button
                            onClick={confirmUpgradeRedirect}
                            className="w-full inline-flex items-center justify-center gap-2 bg-[#064E3B] text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-[#042F2E] transition-colors"
                            data-testid="upgrade-warning-confirm"
                        >
                            Entiendo, suscribirme a {pendingUpgradeLabel}
                        </button>
                        <button
                            onClick={() => setUpgradeWarningOpen(false)}
                            className="text-xs text-[#64748B] hover:text-[#022C22] transition-colors"
                            data-testid="upgrade-warning-cancel"
                        >
                            Volver atrás
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Cancel subscription — schedules cancellation at cycle end,
                keeps Pro access until subscription_expires_at. */}
            <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
                <DialogContent className="rounded-2xl max-w-md" data-testid="cancel-dialog">
                    <DialogHeader>
                        <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                            <Crown className="w-7 h-7 text-slate-500" />
                        </div>
                        <DialogTitle className="font-display text-xl font-semibold text-[#022C22] text-center">
                            ¿Cancelar tu Pyme Pro?
                        </DialogTitle>
                        <DialogDescription className="text-[#334155] text-center text-sm leading-relaxed">
                            Seguís con acceso completo hasta el{" "}
                            <span className="font-semibold text-[#022C22]">
                                {stats?.subscription_expires_at ? fmtSubscriptionDate(stats.subscription_expires_at) : "fin del ciclo"}
                            </span>
                            . Después tu cuenta vuelve al Plan Prueba (20 transacciones al mes).
                        </DialogDescription>
                    </DialogHeader>

                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-[12px] text-slate-700 leading-relaxed">
                        <p className="font-medium mb-1 text-slate-900">Recordá:</p>
                        <p>Cancelá también el auto-cobro en tu panel de Onvo Pay para que no te vuelvan a cobrar el próximo mes.</p>
                    </div>

                    <DialogFooter className="flex-col sm:flex-col gap-2 mt-2">
                        <button
                            onClick={handleCancelSubscription}
                            disabled={cancelLoading}
                            className="w-full inline-flex items-center justify-center gap-2 bg-slate-800 text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-slate-900 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="cancel-confirm-button"
                        >
                            {cancelLoading ? (
                                <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                            ) : null}
                            Confirmar cancelación
                        </button>
                        <button
                            onClick={() => setCancelOpen(false)}
                            className="text-xs text-[#64748B] hover:text-[#022C22] transition-colors"
                            data-testid="cancel-dismiss-button"
                        >
                            Mantener mi plan
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Footer />
        </div>
    );
}
